import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import emailjs from '@emailjs/browser';
import logoSrc from '../assets/logo2.png';
import iconSrc from '../assets/icon.png';

const LOGO_SRC = logoSrc;
const ICON_SRC = iconSrc;

/* ============== UI BASICS ============== */
const ImgWithFallback = ({ src, alt, className = '', text = 'A' }) => {
  const [err, setErr] = useState(false);
  if (err || !src) {
    return (
      <span className={`landing__img-fallback ${className}`} aria-hidden="true">
        {text}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setErr(true)}
      loading="lazy"
      decoding="async"
    />
  );
};

const Pill = ({ children }) => <span className="landing__pill">{children}</span>;

const Kpi = ({ value, label }) => (
  <div className="landing__kpi">
    <div className="landing__kpi-value">{value}</div>
    <div className="landing__kpi-label">{label}</div>
  </div>
);

/* ============== ICONS ============== */
const IconBase = ({ children, size = 24, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const BrainIcon = (props) => (
  <IconBase {...props}>
    <path d="M9 2.5a2.5 2.5 0 0 0-2.5 2.5v1A3.5 3.5 0 0 0 3 9.5V13a3.5 3.5 0 0 0 3.5 3.5H9V21a1.5 1.5 0 0 0 3 0v-4.5c0-1.2.98-2 2.2-2h.8A3.5 3.5 0 0 0 18.5 11V8A3.5 3.5 0 0 0 15 4.5V4A2.5 2.5 0 0 0 12.5 1.5h-1A2.5 2.5 0 0 0 9 4v-.5z" />
    <path d="M9 7.5c.8 0 1.5.7 1.5 1.5V12" />
    <path d="M15 7.5c-.8 0-1.5.7-1.5 1.5V12" />
  </IconBase>
);

const ZapIcon = (props) => (
  <IconBase {...props}>
    <polygon points="13 2 3 14 9 14 7 22 17 10 11 10 13 2" />
  </IconBase>
);

const PlugZapIcon = (props) => (
  <IconBase {...props}>
    <path d="M6 6v6a6 6 0 0 0 12 0V6" />
    <path d="M8 2v4M16 2v4" />
    <polygon points="13 7 9 12 12 12 11 16 15 11 12 11 13 7" />
  </IconBase>
);

const LineChartIcon = (props) => (
  <IconBase {...props}>
    <polyline points="3 17 9 11 13 15 21 7" />
    <path d="M21 17H3" />
  </IconBase>
);

const SearchIcon = (props) => (
  <IconBase {...props}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.5" y2="16.5" />
  </IconBase>
);

const SettingsIcon = (props) => (
  <IconBase {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V22a2 2 0 1 1-4 0v-.17a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.17a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H8a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.17a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V8c0 .67.39 1.28 1 1.54.16.07.33.11.51.11H22a2 2 0 1 1 0 4h-.17c-.18 0-.35.04-.51.11-.61.26-1 .87-1 1.54z" />
  </IconBase>
);

const ShieldCheckIcon = (props) => (
  <IconBase {...props}>
    <path d="M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4z" />
    <path d="M9 12l2 2 4-4" />
  </IconBase>
);

const MailIcon = (props) => (
  <IconBase {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </IconBase>
);

const PhoneIcon = (props) => (
  <IconBase {...props}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.34 1.77.64 2.6a2 2 0 0 1-.45 2.11L8.1 9.59a16 16 0 0 0 6.31 6.31l1.16-1.15a2 2 0 0 1 2.11-.45c.83.3 1.7.52 2.6.64A2 2 0 0 1 22 16.92z" />
  </IconBase>
);

const MessageIcon = (props) => (
  <IconBase {...props}>
    <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8z" />
  </IconBase>
);

const RocketIcon = (props) => (
  <IconBase {...props}>
    <path d="M5 13l4 4" />
    <path d="M15 3a9 9 0 0 0-9 9l6 6a9 9 0 0 0 9-9V6l-3-3h-3z" />
    <circle cx="14" cy="8" r="2" />
  </IconBase>
);

/* ============== SMALL COMPOSITES ============== */
const navItems = [
  { label: 'Serviços', href: '#servicos' },
  { label: 'Como funciona', href: '#como-funciona' },
  { label: 'Tecnologia', href: '#tecnologia' },
  { label: 'Contato', href: '#contato' },
];

const ServiceCard = ({ icon, title, bullets, cta }) => (
  <div className="landing__card">
    <div className="landing__card-header">
      <div className="landing__card-icon">{icon}</div>
      <div>
        <h3 className="landing__card-title">{title}</h3>
        <p>Projetos sob medida para o seu escritório.</p>
      </div>
    </div>
    <ul className="landing__card-list">
      {bullets.map((b, i) => (
        <li key={i} className="landing__card-item">
          <ZapIcon size={16} />
          <span>{b}</span>
        </li>
      ))}
    </ul>
    <div className="landing__card-footer">
      <a href="#contato" className="landing__btn landing__btn--outline">
        {cta}
      </a>
    </div>
  </div>
);

const Step = ({ number, title, desc, icon }) => (
  <div className="landing__step">
    <div className="landing__step-number">{number}</div>
    <div className="landing__step-content">
      <div className="landing__step-icon">{icon}</div>
      <div>
        <h4>{title}</h4>
        <p>{desc}</p>
      </div>
    </div>
  </div>
);

/* ============== PAGE ============== */
const LandingPage = () => {
  const form = useRef(); 
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleContactSubmit = (event) => {
    event.preventDefault();

    // Substitua pelos seus IDs do EmailJS
    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    emailjs.sendForm(serviceID, templateID, form.current, publicKey)
      .then((result) => {
          console.log('E-mail enviado com sucesso!', result.text);
          setIsSubmitted(true); 
      }, (error) => {
          console.error('Falha no envio do e-mail.', error.text);
          alert('Ocorreu um erro ao enviar sua mensagem. Tente novamente.');
      });
  };

  return (
    <div className="landing" id="top">
      <header className="landing__header">
        <div className="landing__container landing__header-content">
          <a href="#top" className="landing__brand" aria-label="Active — Assessoria e Treinamentos">
            <ImgWithFallback
              src={LOGO_SRC}
              alt="Active — Assessoria e Treinamentos"
              className="landing__brand-logo"
              text="A"
            />
            <span className="landing__brand-text">Active</span>
          </a>
          <nav className="landing__nav" aria-label="Navegação principal">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} className="landing__nav-link">
                {item.label}
              </a>
            ))}
          </nav>
          <div className="landing__actions">
            <Link to="/login" className="landing__btn landing__btn--outline">
              Área do Cliente
            </Link>
            <a href="#contato" className="landing__btn landing__btn--primary">
              Falar com especialista
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="landing__hero">
          <div
            className="landing__hero-glow"
            aria-hidden="true"
            style={{ background: 'radial-gradient(closest-side, rgba(28, 136, 229, 0.2), transparent)' }}
          />
          <div className="landing__container landing__hero-grid">
            <div className="landing__hero-content">
              <Pill>Assessoria & Automação para Escritórios de Contabilidade</Pill>
              <h1>
                Mais produtividade com <span className="landing__highlight">inteligência</span> e{' '}
                <span className="landing__highlight">automação</span> sob medida
              </h1>
              <p>
                Criamos soluções personalizadas que eliminam tarefas repetitivas e revelam análises que não seriam possíveis
                manualmente. Integramos com SPED, XMLs de NF-e, bancos e ERPs contábeis via web-services.
              </p>
              <div className="landing__hero-cta">
                <a href="#contato" className="landing__btn landing__btn--primary">
                  <ZapIcon size={18} />
                  Começar um diagnóstico
                </a>
                <a href="#servicos" className="landing__btn landing__btn--outline">
                  <LineChartIcon size={18} />
                  Ver serviços
                </a>
              </div>
            </div>
            <div className="landing__hero-panel">
              <div className="landing__panel">
                <div className="landing__panel-title">
                  <span>Por que Active?</span>
                  <strong>Resultados práticos</strong>
                </div>
                <ul>
                  <li>
                    <ZapIcon size={18} /> Automação sob medida para o seu fluxo
                  </li>
                  <li>
                    <PlugZapIcon size={18} /> Integrações com seu ERP, bancos e serviços
                  </li>
                  <li>
                    <ShieldCheckIcon size={18} /> Segurança, logs e governança
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="servicos" className="landing__section">
          <div className="landing__container">
            <div className="landing__section-header">
              <h2>Serviços</h2>
              <Pill>Personalizados por cliente</Pill>
            </div>
            <div className="landing__grid landing__grid--cards">
              <ServiceCard
                icon={<ZapIcon size={24} />}
                title="Automação de Rotinas"
                bullets={[
                  'Mapeamento de processos do escritório',
                  'Robôs e jobs para eliminar tarefas repetitivas',
                  'Acompanhamento e logs para governança',
                ]}
                cta="Mapear meus processos"
              />
              <ServiceCard
                icon={<PlugZapIcon size={24} />}
                title="Integrações & Importadores"
                bullets={[
                  'Conexão com ERPs contábeis e bancos',
                  'Importadores a partir de extratos/arquivos',
                  'APIs e web-services sob medida',
                ]}
                cta="Quero integrar meus sistemas"
              />
              <ServiceCard
                icon={<LineChartIcon size={24} />}
                title="Auditorias & Insights"
                bullets={[
                  'Cruzamentos automáticos entre fontes fiscais e contábeis',
                  'Detecção de inconsistências e alertas',
                  'Relatórios para priorização de ajustes',
                ]}
                cta="Receber um diagnóstico"
              />
            </div>
          </div>
        </section>

        <section id="como-funciona" className="landing__section">
          <div className="landing__container">
            <div className="landing__section-header">
              <h2>Como funciona</h2>
              <Pill>Do diagnóstico à produção</Pill>
            </div>
            <div className="landing__grid landing__grid--steps">
              <Step number={1} title="Diagnóstico" desc="Entendimento do fluxo atual e objetivos do escritório." icon={<SearchIcon size={20} />} />
              <Step number={2} title="Prototipagem" desc="Provas de conceito rápidas com seus dados (XML, SPED, extratos)." icon={<SettingsIcon size={20} />} />
              <Step number={3} title="Validação" desc="Regras e heurísticas refinadas junto ao time fiscal/contábil." icon={<LineChartIcon size={20} />} />
              <Step number={4} title="Integração" desc="Web-services / APIs para o ERP e bancos, com logs e auditoria." icon={<PlugZapIcon size={20} />} />
              <Step number={5} title="Suporte" desc="Monitoramento, ajustes contínuos e novos cenários." icon={<ShieldCheckIcon size={20} />} />
            </div>
          </div>
        </section>

        <section id="tecnologia" className="landing__section">
          <div className="landing__container landing__tech-grid">
            <div className="landing__tech-content">
              <h2>Tecnologia & Segurança</h2>
              <ul>
                <li>
                  <BrainIcon size={18} /> Regras de negócio + heurísticas inteligentes para dados fiscais.
                </li>
                <li>
                  <SettingsIcon size={18} /> Scripts e serviços escaláveis (Python/Node) com filas e agendamento.
                </li>
                <li>
                  <PlugZapIcon size={18} /> Integrações seguras com ERPs e bancos por APIs/web-services.
                </li>
                <li>
                  <ShieldCheckIcon size={18} /> Criptografia em repouso e trânsito; controle de acesso por ambiente.
                </li>
              </ul>
            </div>
            <div className="landing__kpi-grid">
              <Kpi value="> 1k" label="Documentos processados/mês (Capacidade)" />
              <Kpi value="> 99%" label="Execuções automatizadas" />
              <Kpi value="Auditoria" label="Logs centralizados" />
              <Kpi value="SLA" label="Acordado por cliente" />
            </div>
          </div>
        </section>

        <section id="contato" className="landing__section">
          <div className="landing__container">
            <div className="landing__contact-card">
              <div className="landing__contact-info">
                <h2>Vamos conversar?</h2>
                <p>Conte-nos rapidamente sobre seu cenário e retornaremos com um diagnóstico inicial.</p>
                <div className="landing__contact-links">
                  <a href="mailto:contato@activeat.com.br">
                    <MailIcon size={18} /> contato@activeat.com.br
                  </a>
                  <a href="tel:+555499140555">
                    <PhoneIcon size={18} /> +55 (54) 99914-0555
                  </a>
                  <a href="https://wa.me/555499140555" target="_blank" rel="noreferrer">
                    <MessageIcon size={18} /> WhatsApp
                  </a>
                </div>
              </div>
              {isSubmitted ? (
                // MENSAGEM DE SUCESSO
                <div className="landing__success-message">
                  <ShieldCheckIcon size={48} />
                  <h3>Obrigado pelo seu contato!</h3>
                  <p>Nossa equipe de especialistas retornará em breve.</p>
                </div>
              ) : (
                // FORMULÁRIO ORIGINAL
                <form ref={form} className="landing__contact-form" onSubmit={handleContactSubmit}>
                  <input type="text" name="from_name" placeholder="Nome" required />
                  <input type="email" name="from_email" placeholder="E-mail" required />
                  <input type="text" name="from_phone" placeholder="Telefone (opcional)" />
                  <input type="hidden" name="time" value={new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} />
                  <textarea name="message" rows="4" placeholder="Fale sobre seus processos que gostaria de automatizar" />
                  <button type="submit" className="landing__btn landing__btn--primary landing__btn--full">
                    <RocketIcon size={18} /> Enviar
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="landing__footer">
        <div className="landing__container landing__footer-inner">
          <ImgWithFallback src={ICON_SRC} alt="Active" className="landing__footer-icon" text="A" />
          <span>© {new Date().getFullYear()} Active — Assessoria e Treinamentos</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
